import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { settingsApi } from '@/api/settings';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';
import type { System } from '@/types';

interface FormState {
  code: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  description: '',
  is_active: true,
  sort_order: '0',
};

function mapSystemToForm(system: System): FormState {
  return {
    code: system.code,
    name: system.name,
    description: system.description ?? '',
    is_active: system.is_active,
    sort_order: String(system.sort_order),
  };
}

export default function SystemsSection() {
  const queryClient = useQueryClient();
  const [editingSystem, setEditingSystem] = useState<System | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['settings-systems'],
    queryFn: settingsApi.listSystems,
    staleTime: 30_000,
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSystem(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const sortOrder = Number(form.sort_order);
      if (!form.code.trim() || !form.name.trim()) {
        throw new Error('Code and name are required');
      }
      if (!Number.isFinite(sortOrder)) {
        throw new Error('Sort order must be a number');
      }

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        is_active: form.is_active,
        sort_order: sortOrder,
      };

      return editingSystem
        ? settingsApi.updateSystem(editingSystem.id, payload)
        : settingsApi.createSystem(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-systems'] });
      closeModal();
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: settingsApi.deleteSystem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-systems'] });
    },
  });

  const sortedSystems = useMemo(
    () => (data ?? []).slice().sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [data]
  );

  return (
    <>
      <section className="card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Systems</h2>
            <p className="mt-1 text-sm text-ink-secondary">
              CRUD for admin-portal-owned systems metadata.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setEditingSystem(null);
              setForm(EMPTY_FORM);
              setError('');
              setIsModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add system
          </Button>
        </div>

        {isLoading ? (
          <Loading text="Loading systems..." />
        ) : !sortedSystems.length ? (
          <EmptyState
            icon={<Layers className="h-8 w-8" />}
            title="No systems"
            description="Create the first system to start assigning plans and subscriptions."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border text-left text-xs text-ink-muted">
                  <th className="py-3 pr-4">Code</th>
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Description</th>
                  <th className="py-3 pr-4">Active</th>
                  <th className="py-3 pr-4">Sort</th>
                  <th className="py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-border">
                {sortedSystems.map((system) => (
                  <tr key={system.id}>
                    <td className="py-3 pr-4 font-mono text-xs text-ink-primary">{system.code}</td>
                    <td className="py-3 pr-4 text-ink-primary">{system.name}</td>
                    <td className="py-3 pr-4 text-ink-secondary">{system.description || '—'}</td>
                    <td className="py-3 pr-4 text-ink-secondary">{system.is_active ? 'Yes' : 'No'}</td>
                    <td className="py-3 pr-4 text-ink-secondary">{system.sort_order}</td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setEditingSystem(system);
                            setForm(mapSystemToForm(system));
                            setError('');
                            setIsModalOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void deleteMutation.mutateAsync(system.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingSystem ? 'Edit system' : 'Add system'}
        footer={(
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => void saveMutation.mutateAsync()} loading={saveMutation.isPending}>
              Save
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="Code"
            value={form.code}
            onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
            disabled={!!editingSystem}
            hint="Stable identifier used across plans and subscriptions."
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">Description</label>
            <textarea
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              rows={4}
              className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </div>
          <Input
            label="Sort order"
            type="number"
            value={form.sort_order}
            onChange={(event) => setForm((current) => ({ ...current, sort_order: event.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm text-ink-primary">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
            />
            Active
          </label>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </div>
      </Modal>
    </>
  );
}
